import privateHttpClient from '../privateHttpClient';
import DashboardMapper from '../mappers/DashboardMapper';
import { urlHttpGov } from '../../urls';

export interface IDashboardBaseParams {
    countyId?: number;
    year?: number;
    startDate?: string;
    endDate?: string;
    startPeriod?: string;
    endPeriod?: string;
    signal?: AbortSignal;
}

class DashboardService {
    private baseURL = `${urlHttpGov}/panel/dashboard`;

    private buildParams(params: Record<string, any>) {
        const cleanParams: Record<string, any> = {};
        Object.keys(params).forEach(key => {
            if (params[key] !== undefined && params[key] !== null) {
                cleanParams[key] = params[key];
            }
        });
        return cleanParams;
    }

    async getTotalUsers({ countyId, year, startDate, endDate, startPeriod, endPeriod, signal }: IDashboardBaseParams) {
        const { data } = await privateHttpClient.get(`${this.baseURL}/users`, {
            params: this.buildParams({ countyId, year, startDate, endDate, startPeriod, endPeriod }),
            signal,
        });
        return DashboardMapper.toDomain(data);
    }

    async getTotalInvoices({ countyId, year, startDate, endDate, startPeriod, endPeriod, signal }: IDashboardBaseParams) {
        const { data } = await privateHttpClient.get(`${this.baseURL}/invoices`, {
            params: this.buildParams({ countyId, year, startDate, endDate, startPeriod, endPeriod }),
            signal,
        });
        return DashboardMapper.toInvoicesDomain(data);
    }

    async getTotalPendingInvoices({ countyId, year, startDate, endDate, startPeriod, endPeriod, signal }: IDashboardBaseParams) {
        const { data } = await privateHttpClient.get(`${this.baseURL}/invoices/pending`, {
            params: this.buildParams({ countyId, year, startDate, endDate, startPeriod, endPeriod }),
            signal,
        });
        return DashboardMapper.toPendingInvoicesDomain(data);
    }

    async getInvoicesStats({ countyId, year, startDate, endDate, startPeriod, endPeriod, signal }: IDashboardBaseParams) {
        const { data } = await privateHttpClient.get(`${this.baseURL}/invoices/stats`, {
            params: this.buildParams({ countyId, year, startDate, endDate, startPeriod, endPeriod }),
            signal,
        });
        return DashboardMapper.toInvoicesStatsDomain(data);
    }

    async getInvoicesByType({ countyId, year, startDate, endDate, startPeriod, endPeriod, signal }: IDashboardBaseParams) {
        const { data } = await privateHttpClient.get(`${this.baseURL}/invoices/by-type`, {
            params: this.buildParams({ countyId, year, startDate, endDate, startPeriod, endPeriod }),
            signal,
        });
        return DashboardMapper.toInvoicesByTypeDomain(data);
    }

    async getRegistrationHistory({ countyId, period, year, startDate, endDate, startPeriod, endPeriod, signal }: IDashboardBaseParams & { period: 'day' | 'month' | 'year' }) {
        const { data } = await privateHttpClient.get(`${this.baseURL}/users/registration-history`, {
            params: this.buildParams({ countyId, period, year, startDate, endDate, startPeriod, endPeriod }),
            signal,
        });
        return DashboardMapper.toRegistrationHistoryDomain(data);
    }

    async getInvoicesImportHistory({ countyId, year, startDate, endDate, startPeriod, endPeriod, signal }: IDashboardBaseParams) {
        const { data } = await privateHttpClient.get(`${this.baseURL}/invoices/import-history`, {
            params: this.buildParams({ countyId, year, startDate, endDate, startPeriod, endPeriod }),
            signal,
        });
        return DashboardMapper.toInvoicesImportHistoryDomain(data);
    }

    async getUpcomingRaffles({ countyId, signal }: { countyId?: number; signal: AbortSignal }) {
        const { data } = await privateHttpClient.get(`${this.baseURL}/raffles/upcoming`, {
            params: this.buildParams({ countyId }),
            signal,
        });
        return DashboardMapper.toUpcomingRafflesDomain(data);
    }

    async getTopCompanies({ countyId, year, type, startDate, endDate, startPeriod, endPeriod, signal }: IDashboardBaseParams & { type: string }) {
        const { data } = await privateHttpClient.get(`${this.baseURL}/invoices/top-companies`, {
            params: this.buildParams({
                countyId,
                year,
                type: type !== "todas" ? type : undefined,
                startDate,
                endDate,
                startPeriod,
                endPeriod,
            }),
            signal,
        });
        return DashboardMapper.toTopCompaniesDomain(data);
    }

    async getAllUpcomingRaffles({ countyId, signal }: { countyId?: number; signal: AbortSignal }) {
        const { data } = await privateHttpClient.get(`${this.baseURL}/raffles/upcoming/all`, {
            params: this.buildParams({ countyId }),
            signal,
        });
        return DashboardMapper.toUpcomingRafflesDomain(data);
    }

    async getTopUsers({ countyId, year, type, limit, orderBy, startDate, endDate, startPeriod, endPeriod, signal }: IDashboardBaseParams & { type?: string; limit?: number; orderBy?: string }) {
        const { data } = await privateHttpClient.get(`${this.baseURL}/invoices/top-users`, {
            params: this.buildParams({
                countyId,
                year,
                type: type && type !== 'todas' ? type : undefined,
                limit,
                orderBy,
                startDate,
                endDate,
                startPeriod,
                endPeriod,
            }),
            signal,
        });
        return DashboardMapper.toTopUsersDomain(data);
    }

    async getSocialInstitutionsStats({ countyId, year, startDate, endDate, startPeriod, endPeriod, signal }: IDashboardBaseParams) {
        const { data } = await privateHttpClient.get(`${this.baseURL}/social-institutions`, {
            params: this.buildParams({ countyId, year, startDate, endDate, startPeriod, endPeriod }),
            signal,
        });
        return DashboardMapper.toSocialInstitutionsDomain(data);
    }

    async getFinishedRaffles({ countyId, year, limit, startDate, endDate, startPeriod, endPeriod, signal }: IDashboardBaseParams & { limit?: number }) {
        const { data } = await privateHttpClient.get(`${this.baseURL}/raffles/finished`, {
            params: this.buildParams({ countyId, year, limit, startDate, endDate, startPeriod, endPeriod }),
            signal,
        });
        return DashboardMapper.toFinishedRafflesDomain(data);
    }

    async getLatestPendingRaffles({ countyId, signal }: { countyId?: number; signal: AbortSignal }) {
        const { data } = await privateHttpClient.get(`${this.baseURL}/raffles/latest-pending`, {
            params: this.buildParams({ countyId }),
            signal,
        });
        return DashboardMapper.toUpcomingRafflesDomain(data);
    }
}

const dashboardService = new DashboardService();

export default dashboardService;
